import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalItems } from '@/accessControl/GlobalItems.atom';
import { VersioningSection } from '@/accessControl/VersioningSection.molecule';
import { Checkbox } from '@/core/Checkbox.atom';
import { sqWorksheetStore } from '@/core/core.stores';
import { formatTime, nanosToMillis } from '@/datetime/dateTime.utilities';
import { isLockingAndVersioningEnabled } from '@/services/systemConfiguration.utilities';
import { Icon } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';

export interface LockedMetadataIF {
  lockedAt: number;
  lockedById: string;
  lockedByUsername: string;
}

interface AclModalLockTabProps {
  checkboxHandler: (isChecked: boolean) => void;
  understandLockingIsPermanent: boolean;
  isLocked: boolean;
  lockedMetadata: LockedMetadataIF | undefined;
  userHasManagePermission: boolean;
  closeModalHandler: () => void;
  hasLockingFailedPreviouslyDueToGlobalItems: boolean;
  versionState: VersionState;
}

export const AclModalLockTab = ({
  checkboxHandler,
  understandLockingIsPermanent,
  isLocked,
  lockedMetadata,
  userHasManagePermission,
  hasLockingFailedPreviouslyDueToGlobalItems,
  versionState,
}: AclModalLockTabProps) => {
  const { t } = useTranslation();

  const [metadataTimestamp, setMetadataTimestamp] = useState('unknown');
  const [metadataLockedByUsername, setMetadataLockedByUsername] = useState('unknown');

  useEffect(() => {
    if (lockedMetadata !== undefined) {
      const lockedAtTime = formatTime(nanosToMillis(lockedMetadata.lockedAt), sqWorksheetStore.timezone);

      setMetadataTimestamp(lockedAtTime);
      setMetadataLockedByUsername(lockedMetadata.lockedByUsername);
    }
  }, [lockedMetadata]);

  const LockAnalysisModalContent = () => {
    if (userHasManagePermission && !hasLockingFailedPreviouslyDueToGlobalItems) {
      return (
        <>
          <div className="p10">
            <Icon
              icon="fa-exclamation-triangle"
              type="warning"
              testId="show-error-fallback-popover"
              extraClassNames="mr5"
            />
            {t('ACCESS_CONTROL.LOCKING.WARNING_1')}
          </div>
          <p>{t('ACCESS_CONTROL.LOCKING.DESCRIPTION')}</p>

          {isLockingAndVersioningEnabled() && <VersioningSection versionState={versionState} />}

          <div className="mt10">
            <Checkbox
              id="understand-lock-is-permanent-checkbox"
              isChecked={understandLockingIsPermanent}
              onChange={() => checkboxHandler(!understandLockingIsPermanent)}
              label="ACCESS_CONTROL.LOCKING.UNDERSTAND"
            />
          </div>
        </>
      );
    }

    if (!userHasManagePermission) {
      return <>{t('ACCESS_CONTROL.LOCKING.INSUFFICIENT_PERMISSION_TO_LOCK')}</>;
    }

    return null;
  };

  const LockAnalysis = (
    <div className="flexRowContainer flexFill mt15 overflowVisible" id="aclGrid">
      {LockAnalysisModalContent()}
      <div className="flexRowContainer justify-center tw:w-full mt5 mb10">
        <GlobalItems hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems} />
      </div>
    </div>
  );

  const LockedAnalysisMetadata = (
    <div className="mt50" data-testid="lockSuccessMessage">
      <p>{t('ACCESS_CONTROL.LOCKING.IS_LOCKED')}</p>
      <div>
        <b>{t('ACCESS_CONTROL.LOCKING.LOCKED_ON_DATE')}</b>
        {metadataTimestamp}
      </div>
      <div>
        <b>{t('ACCESS_CONTROL.LOCKING.LOCKED_BY_USER')}</b>
        {metadataLockedByUsername}
      </div>
    </div>
  );

  return <>{isLocked ? LockedAnalysisMetadata : LockAnalysis}</>;
};
